import { AlertTriangle, ArrowRight, FileSignature, Info, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { cheminDossier, LIBELLE_ETAPE, type TableauDeBord } from '@/api/types';
import { Kpi, StatutBadge, Vide } from '@/components/common';
import { resteCorrection } from '@/components/documents';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, heures, montant } from '@/lib/utils';

const EXERCICE_COURANT = new Date().getFullYear();

/** Choix de l'exercice du tableau de bord (filtre période, v1.1 § 9.9). */
export function SelecteurExercice({ exercice, onChange }: { exercice: number; onChange: (e: number) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-muted-foreground">
      Exercice
      <select className="h-9 rounded-md border bg-background px-2 text-sm text-foreground" value={exercice} onChange={(e) => onChange(Number(e.target.value))} aria-label="Exercice du tableau de bord">
        {[EXERCICE_COURANT - 1, EXERCICE_COURANT, EXERCICE_COURANT + 1].map((a) => (
          <option key={a} value={a}>
            {a}
          </option>
        ))}
      </select>
    </label>
  );
}

const GRAVITE = {
  critique: { icone: TriangleAlert, classe: 'border-red-200 bg-red-50 text-red-900' },
  alerte: { icone: AlertTriangle, classe: 'border-amber-200 bg-amber-50 text-amber-900' },
  info: { icone: Info, classe: 'border-slate-200 bg-slate-50 text-slate-800' },
} as const;

/** Alertes consolidées : retards, corrections à échéance, instructions, factures, budget, trésorerie, indicateurs ; chaque alerte ouvre le dossier ou la liste. */
export function Alertes({ alertes }: { alertes: TableauDeBord['alertes'] }) {
  if (!alertes.length) return null;
  return (
    <Card data-testid="bloc-alertes">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Alertes <Badge variant={alertes.some((a) => a.gravite === 'critique') ? 'destructive' : 'secondary'}>{alertes.length}</Badge>
        </CardTitle>
        <CardDescription>Retards, corrections à échéance, instructions, factures, budget, trésorerie et sources d'indicateurs ; les plus graves d'abord.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2 md:grid-cols-2">
        {[...alertes]
          .sort((a, b) => ['critique', 'alerte', 'info'].indexOf(a.gravite) - ['critique', 'alerte', 'info'].indexOf(b.gravite))
          .map((a, k) => {
            const g = GRAVITE[a.gravite];
            return (
              <Link key={`${a.code}-${k}`} to={a.lien} className={cn('flex items-start gap-2 rounded-md border px-3 py-2 text-sm hover:opacity-90', g.classe)}>
                <g.icone className="mt-0.5 size-4 shrink-0" />
                <span>
                  <span className="font-medium">{a.libelle}</span>
                  <span className="block text-xs opacity-80">{a.detail}</span>
                </span>
                <ArrowRight className="ml-auto mt-0.5 size-4 shrink-0 opacity-60" />
              </Link>
            );
          })}
      </CardContent>
    </Card>
  );
}

/** Mes documents (émetteurs) : compteurs cliquables, corrections attendues avec échéance, suivi des circuits. */
export function MesDocuments({ mes, delaiCible }: { mes: TableauDeBord['mesDocuments']; delaiCible: number }) {
  const n = (r: Record<string, number>, ...statuts: string[]) => statuts.reduce((s, st) => s + (r[st] ?? 0), 0);
  const ne = mes.ne ?? {};
  const total = (st: string) => n(mes.tdr, st) + n(mes.fed, st) + n(mes.req ?? {}, st) + n(ne, st);
  const detail = (st: string) => `${n(mes.tdr, st)} TDR · ${n(mes.fed, st)} FED · ${n(mes.req ?? {}, st)} réq. · ${n(ne, st)} note${n(ne, st) > 1 ? 's' : ''}`;
  const totalNotes = Object.values(ne).reduce((s, x) => s + x, 0);
  return (
    <Card data-testid="bloc-mes-documents">
      <CardHeader>
        <CardTitle>Mes documents</CardTitle>
        <CardDescription>Les TDR, FED, réquisitions et notes explicatives que vous avez émis : où ils en sont, ce qui vous attend. Chaque compteur ouvre la liste correspondante.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Link to="/tdr?statut=BROUILLON">
            <Kpi label="En préparation" valeur={total('BROUILLON')} detail={detail('BROUILLON')} />
          </Link>
          <Link to="/tdr?statut=SOUMIS">
            <Kpi label="En validation" valeur={total('SOUMIS')} detail={detail('SOUMIS')} />
          </Link>
          <Link to="/tdr?statut=A_CORRIGER">
            <Kpi label="À corriger" valeur={total('A_CORRIGER')} detail={detail('A_CORRIGER')} accent={total('A_CORRIGER') > 0 ? 'alerte' : undefined} />
          </Link>
          <Link to="/tdr?statut=AUTORISE">
            <Kpi label="Autorisés" valeur={total('AUTORISE')} detail={detail('AUTORISE')} accent="ok" />
          </Link>
          <Link to="/tdr?statut=ANNULE">
            <Kpi label="Rejetés ou annulés" valeur={total('REJETE') + total('ANNULE')} detail={`${total('REJETE')} rejetés · ${total('ANNULE')} annulés`} />
          </Link>
        </div>
        {/* Notes explicatives (GEDEM v3.0, § 7) : état des notes émises, liste et nouvelle note sur un TDR déjà soumis ou autorisé. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border px-3 py-2 text-sm" data-testid="mes-notes">
          <span className="inline-flex items-center gap-2 font-medium">
            <FileSignature className="size-4 text-muted-foreground" /> Notes explicatives
          </span>
          <span className="text-muted-foreground">
            {totalNotes === 0 ? 'aucune note émise' : `${n(ne, 'BROUILLON')} en préparation · ${n(ne, 'SOUMIS')} en validation · ${n(ne, 'A_CORRIGER')} à corriger · ${n(ne, 'AUTORISE')} autorisée${n(ne, 'AUTORISE') > 1 ? 's' : ''}`}
          </span>
          <span className="ml-auto flex gap-4">
            <Link to="/ne" className="font-medium text-primary hover:underline">
              Voir les notes
            </Link>
            <Link to="/ne/nouvelle" className="font-medium text-primary hover:underline">
              Nouvelle note
            </Link>
          </span>
        </div>
        {mes.corrections.length > 0 && (
          <div data-testid="corrections-attendues">
            <div className="mb-2 text-sm font-medium">Corrections attendues</div>
            <div className="space-y-2">
              {mes.corrections.map((c) => (
                <Link key={`${c.type}-${c.id}`} to={cheminDossier(c.type, c.id)} className={cn('flex flex-wrap items-center gap-2 rounded-md border-l-4 px-3 py-2 text-sm', c.joursOuvrablesRestants <= 1 ? 'border-red-500 bg-red-50 text-red-900' : 'border-amber-500 bg-amber-50 text-amber-900')}>
                  <span className="font-semibold">
                    {c.type} {c.numero ?? '(brouillon)'}
                  </span>
                  <span className="truncate">{c.objet}</span>
                  <span className="ml-auto text-xs">
                    à soumettre avant le {dateFr(c.dateLimite)} · {resteCorrection({ dateLimite: c.dateLimite, joursOuvrablesRestants: c.joursOuvrablesRestants, demandeeLe: '', delaiJoursOuvrables: 0, expiree: c.expiree })}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
        {mes.enCircuit.length > 0 && (
          <div data-testid="suivi-circuits">
            <div className="mb-2 text-sm font-medium">Suivi des circuits</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dossier</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Étape actuelle</TableHead>
                  <TableHead className="hidden sm:table-cell">Depuis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mes.enCircuit.map((d) => (
                  <TableRow key={`${d.type}-${d.id}`}>
                    <TableCell>
                      <Link to={cheminDossier(d.type, d.id)} className="font-medium text-primary hover:underline">
                        {d.numero ?? d.type}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-[32ch] truncate">{d.objet}</TableCell>
                    <TableCell className="num text-right">{montant(d.montant)}</TableCell>
                    <TableCell>{d.etape ? LIBELLE_ETAPE[d.etape] : '—'}</TableCell>
                    <TableCell className={cn('hidden sm:table-cell', d.heures > delaiCible && 'text-red-700')}>{heures(d.heures)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {mes.enCircuit.length === 0 && mes.corrections.length === 0 && <Vide message="Aucun document en circuit ni en correction." />}
      </CardContent>
    </Card>
  );
}

/** Répartition des TDR et FED en validation par étape du circuit, avec montants (Direction, DF, contrôle de gestion). */
export function NiveauxCircuit({ niveaux }: { niveaux: TableauDeBord['niveaux'] }) {
  if (!niveaux.length) return null;
  const total = niveaux.reduce((s, n) => ({ tdr: s.tdr + n.tdr.nombre, fed: s.fed + n.fed.nombre, m: s.m + n.tdr.montant + n.fed.montant }), { tdr: 0, fed: 0, m: 0 });
  return (
    <Card data-testid="bloc-niveaux">
      <CardHeader>
        <CardTitle>Dossiers en validation par niveau</CardTitle>
        <CardDescription>
          {total.tdr} TDR et {total.fed} FED en circuit, {montant(total.m)} au total ; un clic ouvre la liste des documents en validation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Étape</TableHead>
              <TableHead className="text-right">TDR</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Montant TDR</TableHead>
              <TableHead className="text-right">FED</TableHead>
              <TableHead className="hidden text-right sm:table-cell">Montant FED</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {niveaux.map((n) => (
              <TableRow key={n.etape}>
                <TableCell>
                  <Link to="/tdr?statut=SOUMIS" className="hover:underline">
                    {LIBELLE_ETAPE[n.etape] ?? n.etape}
                  </Link>
                </TableCell>
                <TableCell className="num text-right">{n.tdr.nombre}</TableCell>
                <TableCell className="num hidden text-right sm:table-cell">{montant(n.tdr.montant)}</TableCell>
                <TableCell className="num text-right">{n.fed.nombre}</TableCell>
                <TableCell className="num hidden text-right sm:table-cell">{montant(n.fed.montant)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** Derniers dossiers du périmètre. */
export function DerniersDossiers({ recents, libelleType }: { recents: TableauDeBord['recents']; libelleType: Record<string, string> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Derniers dossiers</CardTitle>
      </CardHeader>
      <CardContent>
        {recents.length === 0 ? (
          <Vide message="Aucun dossier sur votre périmètre pour le moment." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dossier</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead className="hidden sm:table-cell">Structure</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recents.map((x) => (
                <TableRow key={`${x.type}-${x.id}`}>
                  <TableCell>
                    <Link to={cheminDossier(x.type, x.id)} className="font-medium text-primary hover:underline">
                      {x.numero ?? `${libelleType[x.type]} (brouillon)`}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[32ch] truncate">{x.objet}</TableCell>
                  <TableCell className="hidden sm:table-cell">{x.structureCode}</TableCell>
                  <TableCell className="num text-right">{montant(x.montant)}</TableCell>
                  <TableCell>
                    <StatutBadge statut={x.statut} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
